import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Field, reduxForm, change, formValueSelector } from 'redux-form';
import { Button, ButtonToolbar } from 'reactstrap';
import config from '../../../../config';
import renderSelectField from '../../../../shared/components/form/Select';
import FilterSelect from '../../../../shared/components/filters/FilterSelect';
import Notify from '../../../../shared/components/widgets/Notify';
import useFetch from '../../../../shared/components/hooks/useFetch';

const FORM_NAME = 'userFormModalForm';
const selector = formValueSelector(FORM_NAME);

const accessOptions = [
  { label: 'none', value: 'none' },
  { label: 'account', value: 'account' },
  { label: 'customer', value: 'customer' },
  { label: 'agency', value: 'agency' },
  { label: 'admin', value: 'admin' },
];

// Flex wrapper so a select dropdown and its adjacent "Add"/"Generate" button line up on the same row.
const pickerRowStyle = { display: 'flex', gap: '8px', alignItems: 'center' };
const pickerButtonStyle = { display: 'flex', alignItems: 'center' };

const statusOptions = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 0 },
];

const themeOptions = [
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
  { label: 'Limitless', value: 'limitless' },
];

// Default Access Control templates based on the selected Access scope.
// Per Create User Notes: Account uses the same shape as Customer.
const accessControlTemplates = {
  none: {},
  admin: { scope: 'all' },
  agency: { scope: 'agency', customerIdList: [], accountIdList: [] },
  customer: { scope: 'customer', customerIdList: [], accountIdList: [] },
  account: { scope: 'account', accountIdList: [] },
};

const apiUrl = config.isProdEnv ? config.serverProdUrl : config.serverDevUrl;

// Generates a random, strong password: uppercase, lowercase, numbers, and symbols.
const generatePassword = (length = 16) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const numbers = '23456789';
  const symbols = '!@#$%^&*()-_=+?';
  const allChars = upper + lower + numbers + symbols;

  // Guarantee at least one character from each set, then fill the rest randomly.
  const requiredChars = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];

  const remainingLength = Math.max(length - requiredChars.length, 0);
  const randomChars = Array.from(
    { length: remainingLength },
    () => allChars[Math.floor(Math.random() * allChars.length)],
  );

  const passwordChars = [...requiredChars, ...randomChars];

  // Shuffle so the required characters aren't always in the same positions.
  for (let i = passwordChars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
  }

  return passwordChars.join('');
};

const renderTextField = ({
  input, placeholder, type, meta: { touched, error },
}) => (
  <div className="form__form-group-input-wrap">
    <input {...input} placeholder={placeholder} type={type} />
    {touched && error && <span className="form__form-group-error">{error}</span>}
  </div>
);

renderTextField.propTypes = {
  input: PropTypes.shape().isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
};

renderTextField.defaultProps = {
  placeholder: '',
  type: 'text',
  meta: { touched: false, error: null },
};

const renderTextAreaField = ({
  input, placeholder, meta: { touched, error },
}) => (
  <div className="form__form-group-input-wrap">
    <textarea
      {...input}
      placeholder={placeholder}
      rows="8"
      style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
    />
    {touched && error && <span className="form__form-group-error">{error}</span>}
  </div>
);

renderTextAreaField.propTypes = {
  input: PropTypes.shape().isRequired,
  placeholder: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
};

renderTextAreaField.defaultProps = {
  placeholder: '',
  meta: { touched: false, error: null },
};

const UserFormModalForm = ({
  changeShowModal, setForceRefresh, handleSubmit, editData,
}) => {
  const isEditing = !!editData;
  const user = useSelector(store => store.authentication.user);
  const dispatch = useDispatch();
  const [showError, setShowError] = useState('');
  const [disableButton, setDisableButton] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const accessValue = useSelector(state => selector(state, 'access'));
  const accessControlValue = useSelector(state => selector(state, 'access_control'));

  const { results: specsResults } = useFetch(`${apiUrl}/accounts/specs`);
  const { results: accountsResults } = useFetch(`${apiUrl}/accounts/active-by-name`);

  const customerOptions = (specsResults?.customerData || []).map(cust => ({
    label: `${cust.customer_name} (${cust.customer_id})`,
    value: cust.customer_id,
  }));

  const accountOptions = (Array.isArray(accountsResults) ? accountsResults : []).map(acct => ({
    label: `${acct.seller_account_name} (${acct.seller_account_id})`,
    value: acct.seller_account_id,
  }));

  React.useEffect(() => {
    dispatch(change(FORM_NAME, 'id', editData?.id || 0));
    dispatch(change(FORM_NAME, 'first_name', editData?.first_name || ''));
    dispatch(change(FORM_NAME, 'last_name', editData?.last_name || ''));
    dispatch(change(FORM_NAME, 'email', editData?.email || ''));
    dispatch(change(FORM_NAME, 'access', accessOptions.find(option => option.value === editData?.access) || accessOptions[0]));

    // Pretty-print existing Access Control JSON for editing; fall back to raw value if it isn't valid JSON.
    let prettyAccessControl = '';
    if (editData?.access_control) {
      try {
        prettyAccessControl = JSON.stringify(JSON.parse(editData.access_control), null, 2);
      } catch (error) {
        prettyAccessControl = editData.access_control;
      }
    }
    dispatch(change(FORM_NAME, 'access_control', prettyAccessControl));

    dispatch(change(FORM_NAME, 'password', editData?.password || ''));
    dispatch(change(FORM_NAME, 'temporary_password', editData?.temporary_password || ''));
    dispatch(change(FORM_NAME, 'is_active', statusOptions.find(option => option.value === Number(editData?.is_active)) || statusOptions[0]));
    dispatch(change(FORM_NAME, 'theme', themeOptions.find(option => option.value === editData?.theme) || themeOptions[0]));
    setSelectedCustomer(null);
    setSelectedAccount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  // Auto-populate Access Control only for brand new users when the Access dropdown
  // changes. When editing an existing user we always trust the data loaded from the
  // row, so we never want to overwrite it just because Access happens to re-render.
  React.useEffect(() => {
    if (isEditing) {
      return;
    }
    const template = accessControlTemplates[accessValue?.value] ?? {};
    dispatch(change(FORM_NAME, 'access_control', JSON.stringify(template, null, 2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessValue, isEditing]);

  const handleGeneratePassword = () => {
    dispatch(change(FORM_NAME, 'password', generatePassword()));
  };

  const handleGenerateTemporaryPassword = () => {
    dispatch(change(FORM_NAME, 'temporary_password', generatePassword()));
  };

  const addIdToList = (listName, idValue) => {
    if (idValue === null || idValue === undefined) {
      return;
    }
    let parsed;
    try {
      parsed = accessControlValue ? JSON.parse(accessControlValue) : {};
    } catch (error) {
      setShowError('Access Control must be valid JSON before you can add IDs to it.');
      return;
    }
    setShowError('');
    const currentList = Array.isArray(parsed[listName]) ? parsed[listName] : [];
    if (!currentList.includes(idValue)) {
      parsed[listName] = [...currentList, idValue];
    } else {
      parsed[listName] = currentList;
    }
    dispatch(change(FORM_NAME, 'access_control', JSON.stringify(parsed, null, 2)));
  };

  const submitForm = (formData) => {
    let flattenedAccessControl = '';
    try {
      flattenedAccessControl = JSON.stringify(formData.access_control ? JSON.parse(formData.access_control) : {});
    } catch (error) {
      setShowError('Access Control must be valid JSON.');
      return;
    }

    setDisableButton(true);
    setShowError('');

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        id: isEditing ? editData.id : 0,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        access: formData.access?.value,
        access_control: flattenedAccessControl,
        password: formData.password,
        temporary_password: formData.temporary_password,
        is_active: formData.is_active?.value,
        username: isEditing ? (editData.username || '') : '',
        verified: isEditing ? (editData.verified || 0) : 0,
        theme: formData.theme?.value,
      }),
    };

    fetch(`${apiUrl}/users`, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw results;
        }
        return results.json();
      }).then((data) => {
        Notify.show('primary', 'Success', `User entry ${data[0].id} has been ${isEditing ? 'updated' : 'created'}.`);
        setDisableButton(false);
        setForceRefresh();
        changeShowModal();
      }).catch((error) => {
        error.json().then((data) => {
          setShowError(`Problem ${isEditing ? 'updating' : 'adding'} User entry. \n Reference: ${JSON.stringify(data)}`);
          setDisableButton(false);
        }).catch(() => {
          setShowError(`Problem ${isEditing ? 'updating' : 'adding'} User entry. \n Reference: ${error}`);
          setDisableButton(false);
        });
      });
  };

  const showCustomerPicker = accessValue && ['agency', 'customer'].includes(accessValue.value);
  const showAccountPicker = accessValue && ['agency', 'customer', 'account'].includes(accessValue.value);

  return (
    <form className="form" onSubmit={handleSubmit(submitForm)}>
      <h2>{isEditing ? 'Edit User' : 'Add User'}</h2>
      <div className="form__form-group"><br /></div>
      <div className="form__form-group">
        <span className="form__form-group-label">First Name</span>
        <div className="form__form-group-field">
          <Field name="first_name" component={renderTextField} type="text" placeholder="First Name" />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Last Name</span>
        <div className="form__form-group-field">
          <Field name="last_name" component={renderTextField} type="text" placeholder="Last Name" />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Email</span>
        <div className="form__form-group-field">
          <Field name="email" component={renderTextField} type="text" placeholder="Email" />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Access</span>
        <div className="form__form-group-field">
          <Field name="access" component={renderSelectField} type="text" options={accessOptions} />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Access Control</span>
        <div className="form__form-group-field">
          <Field name="access_control" component={renderTextAreaField} placeholder='{"scope": "account", "customerIdList": [], "accountIdList": []}' />
        </div>
      </div>
      {showCustomerPicker && (
        <div className="form__form-group">
          <span className="form__form-group-label">Add Customer to Access Control</span>
          <div className="form__form-group-field" style={pickerRowStyle}>
            <div style={{ flex: 1 }}>
              <FilterSelect
                name="customerPicker"
                label=""
                selectClass=""
                options={customerOptions}
                selected={selectedCustomer}
                onChange={option => setSelectedCustomer(option)}
              />
            </div>
            <div style={pickerButtonStyle}>
              <Button type="button" size="sm" className="m-0" onClick={() => addIdToList('customerIdList', selectedCustomer?.value)}>Add</Button>
            </div>
          </div>
        </div>
      )}
      {showAccountPicker && (
        <div className="form__form-group">
          <span className="form__form-group-label">Add Account to Access Control</span>
          <div className="form__form-group-field" style={pickerRowStyle}>
            <div style={{ flex: 1 }}>
              <FilterSelect
                name="accountPicker"
                label=""
                selectClass=""
                options={accountOptions}
                selected={selectedAccount}
                onChange={option => setSelectedAccount(option)}
              />
            </div>
            <div style={pickerButtonStyle}>
              <Button type="button" size="sm" className="m-0" onClick={() => addIdToList('accountIdList', selectedAccount?.value)}>Add</Button>
            </div>
          </div>
        </div>
      )}
      <div className="form__form-group">
        <span className="form__form-group-label">Password</span>
        <div className="form__form-group-field" style={pickerRowStyle}>
          <div style={{ flex: 1 }}>
            <Field name="password" component={renderTextField} type="text" placeholder="Password" />
          </div>
          <div style={pickerButtonStyle}>
            <Button type="button" size="sm" className="m-0" onClick={handleGeneratePassword}>Generate</Button>
          </div>
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Temporary Password</span>
        <div className="form__form-group-field" style={pickerRowStyle}>
          <div style={{ flex: 1 }}>
            <Field name="temporary_password" component={renderTextField} type="text" placeholder="Temporary Password" />
          </div>
          <div style={pickerButtonStyle}>
            <Button type="button" size="sm" className="m-0" onClick={handleGenerateTemporaryPassword}>Generate</Button>
          </div>
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Status</span>
        <div className="form__form-group-field">
          <Field name="is_active" component={renderSelectField} type="text" options={statusOptions} />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">Default Theme</span>
        <div className="form__form-group-field">
          <Field name="theme" component={renderSelectField} type="text" options={themeOptions} />
        </div>
      </div>
      {showError && (
        <div className="form__form-group-error">&nbsp;&nbsp;&nbsp;Error: {showError}</div>
      )}
      <div className="form__form-group"><br /></div>
      <ButtonToolbar className="form__button-toolbar">
        <Button color="primary" type="submit" disabled={disableButton}>
          {isEditing ? 'Update User' : 'Create User'}
        </Button>
        <Button type="button" onClick={changeShowModal}>Cancel</Button>
      </ButtonToolbar>
    </form>
  );
};

UserFormModalForm.propTypes = {
  changeShowModal: PropTypes.func.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  // eslint-disable-next-line
  editData: PropTypes.object,
};

UserFormModalForm.defaultProps = {
  editData: null,
};

const validate = (values) => {
  const errors = {};
  if (!values.first_name) {
    errors.first_name = 'First Name is required';
  }
  if (!values.last_name) {
    errors.last_name = 'Last Name is required';
  }
  if (!values.email) {
    errors.email = 'Email is required';
  }
  if (!values.password) {
    errors.password = 'Password is required';
  }
  if (!values.access?.value) {
    errors.access = 'Access is required';
  }
  if (values.access_control) {
    try {
      JSON.parse(values.access_control);
    } catch (error) {
      errors.access_control = 'Access Control must be valid JSON';
    }
  } else {
    errors.access_control = 'Access Control is required';
  }
  return errors;
};

export default reduxForm({
  form: FORM_NAME,
  validate,
})(UserFormModalForm);
